import React, { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { RadioGroup, Input, TextArea, ColorPicker } from '@progress/kendo-react-inputs';
import { MultiSelect } from "@progress/kendo-react-dropdowns";
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import PublishSwitch from '../publishSwitch';
import VideoPreview, { youtube_parser } from '../../VideoPreview';
import Uploader, { uploaderTypes } from '../../Uploader';
import ListEditor from '../../ListEditor';
import { LocalizationContext } from '../../../contexts';
import './index.scss';
import '../forms.scss';

export default function TextMediaBanner({ data, settingsData = {}, disciplinesList, ageGradesList, sectorsList, onUpdate, validator, ...rest }) {
  const emptyItem = useRef({
    name: "",
    title: "",
    text: "",
    type: "image",
    image: "",
    image_mobile: "",
    animate_json: "",
    animate_json_mobile: "",
    video: "",
    video_poster: "",
    video_poster_mobile: "",
    has_button: false,
    button: false,
    isPublish: false
  });
  const generalEmptyItem = useRef({ ...emptyItem.current });
  const personalEmptyItem = useRef({
    ...emptyItem.current,
    ageGrades: [],
    disciplines: [],
    sectors: []
  });
  const buttonEmptyItem = { text: "", link: "", link_target: "_top" };
  const [compData, setCompData] = useState({});
  const [localization] = useContext(LocalizationContext);
  const { labels } = localization;
  const userTypes = [ //for Personal tab
    { id: "teacher", text: localization.labels.bannersManagement.userTypes.teacher },
    { id: "coordinator", text: localization.labels.bannersManagement.userTypes.coordinator, itemDisabled: true },
    { id: "director", text: localization.labels.bannersManagement.userTypes.director, itemDisabled: true },
  ]
  const [contentSelect, setContentSelect] = useState(0);

  const handleContentSelect = (e) => {
    setContentSelect(e.selected);
  }

  validator.current = () => {
    return true;
  };

  useEffect(() => {
    let defaultData = { items: [settingsData.type === "general" ? { ...generalEmptyItem.current } : { ...personalEmptyItem.current }] };
    const startCompData = { ...defaultData, ...JSON.parse(JSON.stringify(data ? data : {})) };
    if (startCompData.items.length === 0) {
      startCompData.items = [settingsData.type === "general" ? { ...generalEmptyItem.current } : { ...personalEmptyItem.current }];
    }
    setCompData(startCompData);
  }, [data]);

  const canPublish = (item) => {
    if (!(item.name && item.text)) {
      return false;
    }
    if ((!item.type || item.type === "image") && !(item.image && item.image_mobile)) {
      return false;
    }
    if (item.type === "animate" && !(item.animate_json && item.animate_json_mobile)) {
      return false;
    }
    if (item.type === "video" && !(item.video && item.video_poster && item.video_poster_mobile)) {
      return false;
    }
    if (item.has_button && settingsData.buttonDisabled !== true) {
      if (!item.button) {
        return false;
      }
      if (!(item.button.text && item.button.link)) {
        return false;
      }
    }
    return true;
  };

  const isEmptyItem = () => {
    return JSON.stringify(compData) === JSON.stringify(emptyItem.current)
  }

  const showRequired = (requiredField) => {
    return (!isEmptyItem() && !requiredField)
  }

  const updateItem = useCallback((i, newData, key) => {
    let newCompData;
    if (i !== null) {
      newCompData = { ...compData };
      if (key) {
        newCompData.items[i][key] = newData;
      }
      else {
        newCompData.items[i] = { ...newCompData.items[i], ...newData };
      }
      newCompData.items[i].isPublish = newCompData.items[i].isPublish && canPublish(newCompData.items[i]);
    } else {
      newCompData = { ...compData, ...newData };
    }
    setCompData(newCompData);
    onUpdate(newCompData);
  }, [compData]);

  const updateItemHasButton = useCallback((i, newData) => {
    const newCompData = { ...compData };
    newCompData.items[i] = { ...newCompData.items[i], ...newData };
    if (newData.has_button) {
      newCompData.items[i].button = { ...buttonEmptyItem }
    } else {
      newCompData.items[i].button = false
    }
    newCompData.items[i].isPublish = newCompData.items[i].isPublish && canPublish(newCompData.items[i]);

    setCompData(newCompData);
    onUpdate(newCompData);
  }, [compData]);

  const updateItemButton = useCallback((i, newData) => {
    const newCompData = { ...compData };
    newCompData.items[i].button = { ...newCompData.items[i].button, ...newData };
    newCompData.items[i].isPublish = newCompData.items[i].isPublish && canPublish(newCompData.items[i]);
    setCompData(newCompData);
    onUpdate(newCompData);
  }, [compData]);

  const title = (settings) => {
    return settingsData.title ? eval("`" + settingsData.title + "`") : `[${settings.listLength}/${settings.maxSize}]`;
  };

  const personalTabs = () => {
    return <><div className="form-tabs">
      <TabStrip selected={contentSelect} onSelect={handleContentSelect} animation={false}>
        {userTypes.map((tab, i) => {
          return <TabStripTab key={i} title={tab.text}></TabStripTab>;
        })}
      </TabStrip>
    </div></>
  };

  const itemTitle = (item, settings) => {
    return `${labels.pagesManagement.textMediaBanner.banner} ${settings.index + 1}: ${item.name} (${labels.pagesManagement.textMediaBanner["display_type_" + (item.type || "image")]}) (${!canPublish(item) ? labels.pagesManagement.forms.publish_not_ready : item.isPublish ? labels.pagesManagement.forms.is_publish : labels.pagesManagement.forms.publish_ready})`;
  };

  const itemContent = (item, settings) => {
    return <>
      {settingsData.type === 'personal' && <>
        {ageGradesList && ageGradesList.length && <div className="form-field">
          <label className="form-field__label">{labels.pagesManagement.textMediaBanner.add_age_grades}</label>
          <MultiSelect
            dir={labels.dir}
            data={ageGradesList}
            value={item.ageGrades ? item.ageGrades.map(i => ageGradesList.find(d => d.id === i)) : []}
            onChange={({ value }) => {
              updateItem(settings.index, value.map(i => i.id), 'ageGrades');
            }}
            textField={labels.dimensions_textField}
            dataItemKey="id"
          />
        </div>}
        {disciplinesList && disciplinesList.length && <div className="form-field">
          <label className="form-field__label">{labels.pagesManagement.textMediaBanner.add_disciplines}</label>
          <MultiSelect
            dir={labels.dir}
            data={disciplinesList}
            value={item.disciplines ? item.disciplines.map(i => disciplinesList.find(d => d.id === i)) : []}
            onChange={({ value }) => {
              updateItem(settings.index, value.map(i => i.id), 'disciplines');
            }}
            textField={labels.dimensions_textField}
            dataItemKey="id"
          />
        </div>}
        {sectorsList && sectorsList.length && <div className="form-field">
          <label className="form-field__label">{labels.pagesManagement.textMediaBanner.add_sectors}</label>
          <MultiSelect
            dir={labels.dir}
            data={sectorsList}
            value={item.sectors ? item.sectors.map(i => sectorsList.find(d => d.id === i)) : []}
            onChange={({ value }) => {
              updateItem(settings.index, value.map(i => i.id), 'sectors');
            }}
            textField={labels.dimensions_textField}
            dataItemKey="id"
          />
        </div>}
      </>}

      <div className="form-field">
        <label className="form-field__label mandatory">{labels.pagesManagement.textMediaBanner.add_name}</label>
        <Input name={'name'} value={item.name} onChange={({ value }) => updateItem(settings.index, value, 'name')}></Input>
        {showRequired(item.name) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
      </div>
      <div className="form-field">
        <label className="form-field__label mandatory">{labels.pagesManagement.textMediaBanner.add_title}</label>
        <Input name={'title'} value={item.title} onChange={({ value }) => updateItem(settings.index, value, 'title')}></Input>
        {showRequired(item.title) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
      </div>
      <div className="form-field">
        <label className="form-field__label mandatory">{labels.pagesManagement.textMediaBanner.add_text}</label>
        <TextArea name={'text'} value={item.text} onChange={({ value }) => updateItem(settings.index, (settingsData.maxTextSize && value.length > settingsData.maxTextSize ? value.substring(0, settingsData.maxTextSize) : value), 'text')}></TextArea>
        {settingsData.maxTextSize && <div className="k-form-hint">{`${item.text.length}/${settingsData.maxTextSize}`}</div>}
        {showRequired(item.text) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
      </div>

      <div className="form-field">
        <label className="form-field__label inline">{labels.pagesManagement.textMediaBanner.display_type}</label>
        <RadioGroup
          data={[
            { label: labels.pagesManagement.textMediaBanner.display_type_image, value: "image" },
            { label: labels.pagesManagement.textMediaBanner.display_type_animate, value: "animate" },
            { label: labels.pagesManagement.textMediaBanner.display_type_video, value: "video" },
          ]}
          layout='horizontal'
          value={item.type || "image"}
          onChange={({ value }) => {
            updateItem(settings.index, { type: value });
          }}
        />
      </div>

      {(!item.type || item.type === "image") && <>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_pc_banner}</label>
          <Uploader type={uploaderTypes.image} imageUrl={item.image} prefix={'textImageBanner'} onChange={(file) => { updateItem(settings.index, { image: file }) }} />
        </div>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_mobile_banner}</label>
          <Uploader type={uploaderTypes.image} imageUrl={item.image_mobile} prefix={'textImageBanner'} onChange={(file) => { updateItem(settings.index, { image_mobile: file }) }} />
        </div>
      </>}
      {item.type === "animate" && <>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_pc_animation}</label>
          <Uploader type={uploaderTypes.animation} animationUrl={item.animate_json} prefix={'textImageBanner'} onChange={(file) => { updateItem(settings.index, { animate_json: file }) }} />
        </div>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_mobile_animation}</label>
          <Uploader type={uploaderTypes.animation} animationUrl={item.animate_json_mobile} prefix={'textImageBanner'} onChange={(file) => { updateItem(settings.index, { animate_json_mobile: file }) }} />
        </div>
      </>}
      {item.type === "video" && <>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_youtube_link}</label>
          <Input name={'video_url'} value={item.video} style={{ direction: 'ltr', textAlign: 'left' }} onChange={({ value }) => updateItem(settings.index, value, 'video')}></Input>
          <VideoPreview videoUrl={item.video} />
          {showRequired(item.video) && <div className="form-field__required">{labels.pagesManagement.forms.required}</div>}
        </div>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.textMediaBanner.add_video_poster}</label>
          <Uploader type={uploaderTypes.image} imageUrl={item.video_poster} prefix={'textImageBanner'} onChange={(file) => { updateItem(settings.index, { video_poster: file }) }} />
        </div>
        <div className="form-field">
          <label className="form-field__label mandatory">{labels.pagesManagement.textMediaBanner.add_video_poster_mobile}</label>
          <Uploader type={uploaderTypes.image} imageUrl={item.video_poster_mobile} prefix={'textImageBanner'} onChange={(file) => { updateItem(settings.index, { video_poster_mobile: file }) }} />
        </div>
      </>}

      {settingsData.buttonDisabled !== true && <div className="form-field">
        <label className="form-field__label inline">{labels.pagesManagement.forms.action_button}</label>
        <RadioGroup
          data={[
            { label: labels.pagesManagement.forms.without_button, value: false },
            { label: labels.pagesManagement.forms.with_button, value: true }
          ]}
          layout='horizontal'
          value={!!item.has_button}
          onChange={({ value }) => {
            updateItemHasButton(settings.index, { has_button: value });
          }}
        />
      </div>}
      {item.has_button && item.button && settingsData.buttonDisabled !== true &&
        <>
          <div className="form-field">
            <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_button_text}</label>
            <Input name={'buttonText'} value={item.button.text} onChange={({ value }) => updateItemButton(settings.index, { text: value })}></Input>
          </div>
          <div className="form-field">
            <label className="form-field__label mandatory">{labels.pagesManagement.forms.add_link}</label>
            <div className="form-field--link">
              <Input name={'buttonLink'} value={item.button.link} onChange={({ value }) => updateItemButton(settings.index, { link: value })}></Input>
              <RadioGroup
                data={[
                  { label: labels.pagesManagement.forms.open_top, value: '_top' },
                  { label: `${labels.pagesManagement.forms.open_blank} (${labels.pagesManagement.forms.for_external_links})`, value: '_blank' }
                ]}
                layout='horizontal'
                value={item.button['link_target']}
                onChange={({ value }) => {
                  updateItemButton(settings.index, { link_target: value });
                }}
              />
            </div>
          </div>
        </>
      }

      {settingsData.setBackgroundColor && <>
        <div className="form-field">
          <label className="form-field__label inline">{labels.pagesManagement.forms.background_color}</label>
          <RadioGroup
            data={[
              { label: labels.pagesManagement.forms.set_background_color_false, value: false },
              { label: labels.pagesManagement.forms.set_background_color_true, value: true }
            ]}
            layout='horizontal'
            value={!!item.has_background_color}
            onChange={({ value }) => {
              updateItem(settings.index, { has_background_color: value });
            }}
          />
          {item.has_background_color && <ColorPicker
            dir="ltr"
            value={item.background_color}
            view={"gradient"}
            onChange={({ value }) => {
              console.log(value);
              updateItem(settings.index, { background_color: value });
            }}
            gradientSettings={{
              opacity: true,
            }}
          />}
        </div>
      </>}

      <PublishSwitch isPublish={item?.isPublish} canPublish={canPublish(item)}
        updateItem={(event) => updateItem(settings.index, { isPublish: event.target.value })} />
    </>;
  };

  return <>
    {settingsData.tabs ? personalTabs() : null}
    <ListEditor
      isSortable={true}
      canRemove={true}
      canAdd={true}
      removeContentOnClose={false}
      maxSize={settingsData.maxSize || 3}
      minSize={settingsData.minSize || 0}
      className="textMediaBanner-listEditor"
      addNewItemBtnText={labels.pagesManagement.textMediaBanner.addNewItemBtnText}
      title={title}
      emptyItem={settingsData.type === "general" ? generalEmptyItem.current : personalEmptyItem.current}
      itemTitle={itemTitle}
      itemContent={itemContent}
      list={[...(compData.items || [])]}
      onListUpdate={(newList) => { updateItem(null, { items: newList }) }}
    />
  </>
};